import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BeakerIcon } from '@heroicons/react/24/outline'
import { bloodTestsAPI, healthCyclesAPI } from '../../services/api'
import { errorCode, formatDate } from './cycleStatus'
import { cyclePath } from './useStartHealthCycle'

// VH-233: after a partial panel is uploaded — "Add to cycle <date> as a supplementary test?"
// Attach recomputes the cycle (and opens it); Keep separate is remembered for this test.
export function AttachSupplementaryPrompt({ bloodTestId, enabled = true }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [error, setError] = useState(null)
  const queryKey = ['bloodTestCycleSuggestion', bloodTestId]

  const { data: suggestion } = useQuery({
    queryKey,
    queryFn: () => bloodTestsAPI.getCycleSuggestion(bloodTestId),
    select: (response) => response.data?.cycle_suggestion ?? null,
    enabled: enabled && !!bloodTestId,
  })

  const attach = useMutation({
    mutationFn: () => healthCyclesAPI.attachSupplementaryTest(suggestion.cycle_id, bloodTestId),
    onSuccess: () => {
      setError(null)
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ['healthCycle', suggestion.cycle_id] })
      queryClient.invalidateQueries({ queryKey: ['healthCycles'] })
      navigate(cyclePath(suggestion.cycle_id))
    },
    onError: (err) => setError(t(`healthCycle.errors.${errorCode(err)}`, t('bloodTests.cycleSuggestion.attachFailed'))),
  })

  const dismiss = useMutation({
    mutationFn: () => bloodTestsAPI.dismissCycleSuggestion(bloodTestId),
    onSuccess: () => {
      setError(null)
      queryClient.setQueryData(queryKey, (old) => (old ? { ...old, data: { ...old.data, cycle_suggestion: null } } : old))
    },
    onError: () => setError(t('bloodTests.cycleSuggestion.dismissFailed')),
  })

  if (!suggestion) return null
  const busy = attach.isPending || dismiss.isPending

  return (
    <section className="rounded-lg border border-primary-200 bg-primary-50 p-4" role="alertdialog"
      aria-labelledby={`cycle-suggestion-${bloodTestId}`} data-testid="attach-supplementary-prompt">
      <div className="flex items-start gap-3">
        <BeakerIcon className="h-6 w-6 flex-shrink-0 text-primary-600" aria-hidden="true" />
        <div className="min-w-0 space-y-2">
          <p id={`cycle-suggestion-${bloodTestId}`} className="text-sm font-semibold text-gray-900">
            {t('bloodTests.cycleSuggestion.question', { date: formatDate(suggestion.cycle_date, i18n.language) })}
          </p>
          <p className="text-sm text-gray-700">{t('bloodTests.cycleSuggestion.explanation')}</p>
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-primary text-sm" disabled={busy} onClick={() => attach.mutate()}>
              {attach.isPending ? t('bloodTests.cycleSuggestion.attaching') : t('bloodTests.cycleSuggestion.attach')}
            </button>
            <button type="button" className="btn-outline text-sm" disabled={busy} onClick={() => dismiss.mutate()}>
              {t('bloodTests.cycleSuggestion.keepSeparate')}
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AttachSupplementaryPrompt
